import { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { isPendoInitializedAtom } from './isPendoInitializedAtom';
import { pendoApiKeyAtom } from './pendoApiKey';

export type PendoTrackProperties = Record<string, unknown>;

export type Pendo<T> = {
	initialize: (config: T) => unknown;
	track: (eventName: string, properties?: PendoTrackProperties) => unknown;
	identify?: (config: T) => unknown;
	clearSession?: () => unknown;
};

// The Pendo agent transforms reported URLs at initialize time so that, for
// example, every `/scorecard?date=YYYY-MM-DD` deep link reports as the same
// page. The shape mirrors the runtime config the Pendo agent accepts.
export type PendoLocationTransform = {
	attr: 'pathname' | 'search' | 'hash' | 'href';
	action: 'Replace' | 'Exclude';
	data: (oldValue: string, location: Location) => string;
};

declare const pendo: Pendo<unknown> | undefined;

export const usePendo = <T>(pendoMetadata: T, apiKey: string): void => {
	const [, setPendoApiKey] = useAtom(pendoApiKeyAtom);

	const isPendoInitialized = useAtomValue(isPendoInitializedAtom);

	useEffect(() => {
		if (apiKey) {
			setPendoApiKey(apiKey);
		}
	}, []);

	useEffect(() => {
		if (!pendoMetadata || !isPendoInitialized) {
			return;
		}

		if (!pendo) {
			return;
		}

		pendo.initialize(pendoMetadata);
	}, [pendoMetadata, isPendoInitialized]);
};
